import React from 'react';
import { ScanFace, Eye, Lock, Globe, FolderSearch, Shield } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { Button } from '../UI/Button';
import wizardStyles from './LiveAlbumWizard.module.css';
import s from './FaceRecognitionStep.module.css';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface FaceRecognitionConfig {
  faceRecognitionEnabled: boolean;
  guestVisibility: 'all' | 'matched_only';
  faceSearchScope: 'album' | 'event';
}

interface FaceRecognitionStepProps {
  config: FaceRecognitionConfig;
  onChange: (patch: Partial<FaceRecognitionConfig>) => void;
  isLive: boolean;
  onNext: () => void;
  onBack: () => void;
}

// ─── Option Card ────────────────────────────────────────────────────────────

function OptionCard({
  selected,
  onClick,
  icon,
  label,
  description,
  tag,
}: {
  selected: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  description: string;
  tag: string;
}) {
  return (
    <button
      type="button"
      className={[s.option, selected ? s['option-selected'] : ''].filter(Boolean).join(' ')}
      onClick={onClick}
    >
      <div className={[s.radio, selected ? s['radio-selected'] : ''].filter(Boolean).join(' ')}>
        {selected && <div className={s['radio-dot']} />}
      </div>
      <div className={s['option-body']}>
        <div className={s['option-label-row']}>
          {icon}
          <span className={s['option-label']}>{label}</span>
        </div>
        <p className={s['option-desc']}>{description}</p>
        <span className={s['option-tag']}>{tag}</span>
      </div>
    </button>
  );
}

// ─── Component ──────────────────────────────────────────────────────────────

export function FaceRecognitionStep({
  config,
  onChange,
  isLive,
  onNext,
  onBack,
}: FaceRecognitionStepProps) {
  const { faceRecognitionEnabled, guestVisibility, faceSearchScope } = config;

  return (
    <div className={wizardStyles['step-col']}>
      <div className={wizardStyles['step-scroll']}>
        <div className={s.container}>
          {/* Hero toggle */}
          <button
            type="button"
            className={[s['hero-card'], faceRecognitionEnabled ? s['hero-card-active'] : ''].filter(Boolean).join(' ')}
            onClick={() => onChange({ faceRecognitionEnabled: !faceRecognitionEnabled })}
            role="switch"
            aria-checked={faceRecognitionEnabled}
          >
            <div className={[s['hero-icon-wrap'], faceRecognitionEnabled ? s['hero-icon-wrap-active'] : ''].filter(Boolean).join(' ')}>
              <ScanFace size={22} style={{ color: 'var(--clay-orange)' }} />
            </div>
            <div className={s['hero-body']}>
              <p className={s['hero-title']}>Enable Face Recognition</p>
              <p className={s['hero-subtitle']}>
                Let guests upload a selfie to instantly find every photo they appear in
              </p>
            </div>
            <div className={[s['toggle-track'], faceRecognitionEnabled ? s['toggle-track-on'] : s['toggle-track-off']].join(' ')}>
              <div className={[s['toggle-thumb'], faceRecognitionEnabled ? s['toggle-thumb-on'] : s['toggle-thumb-off']].join(' ')} />
            </div>
          </button>

          {/* Expanded options */}
          <AnimatePresence>
            {faceRecognitionEnabled && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                style={{ overflow: 'hidden' }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                  {/* Guest visibility */}
                  <div>
                    <p className={s['section-header']}>Guest visibility</p>
                    <div className={s.options}>
                      <OptionCard
                        selected={guestVisibility === 'all'}
                        onClick={() => onChange({ guestVisibility: 'all' })}
                        icon={<Eye size={15} style={{ color: 'var(--clay-orange)' }} />}
                        label="All photos + My Photos filter"
                        description="Guests browse every photo, plus a &quot;My Photos&quot; tab showing only photos they appear in."
                        tag="Open galleries"
                      />
                      <OptionCard
                        selected={guestVisibility === 'matched_only'}
                        onClick={() => onChange({ guestVisibility: 'matched_only' })}
                        icon={<Lock size={15} style={{ color: 'var(--clay-orange)' }} />}
                        label="Only their photos + highlights"
                        description="Guests see ONLY photos they appear in, plus any photos you pin as highlights."
                        tag="Personal delivery"
                      />
                    </div>
                  </div>

                  {/* Face search scope — only for manual albums */}
                  {!isLive && (
                    <div>
                      <p className={s['section-header']}>Search scope</p>
                      <div className={s.options}>
                        <OptionCard
                          selected={faceSearchScope === 'album'}
                          onClick={() => onChange({ faceSearchScope: 'album' })}
                          icon={<FolderSearch size={15} style={{ color: 'var(--clay-orange)' }} />}
                          label="This album only"
                          description="Match faces against the photos you selected for this album."
                          tag="Curated albums"
                        />
                        <OptionCard
                          selected={faceSearchScope === 'event'}
                          onClick={() => onChange({ faceSearchScope: 'event' })}
                          icon={<Globe size={15} style={{ color: 'var(--clay-orange)' }} />}
                          label="All event photos"
                          description="Match faces against every photo from the event — guests may discover additional photos."
                          tag="Full event coverage"
                        />
                      </div>
                    </div>
                  )}

                  {/* Privacy callout */}
                  <div className={s.callout}>
                    <Shield size={16} className={s['callout-icon']} />
                    <span>
                      Selfies are kept private and only used for matching.
                      {guestVisibility === 'matched_only'
                        ? ' Guests must provide a selfie to see their photos.'
                        : ' Guests can skip the selfie and browse all photos.'}
                    </span>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <div className={wizardStyles['step-footer']}>
        <Button variant="ghost" onClick={onBack}>&larr; Back</Button>
        <Button onClick={onNext}>Next</Button>
      </div>
    </div>
  );
}
